import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { axiosInstance } from '@/App';
import { toast } from 'sonner';
import { format, getDaysInMonth, getDay } from 'date-fns';
import {
  Users,
  UserCheck,
  Shield,
  AlertTriangle,
  Timer,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Download,
  Search,
} from 'lucide-react';

// ─── Month names ─────────────────────────────────────────────────────────
const MONTH_NAMES = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

// ─── Stat card (reuse pattern from AttendanceDashboard) ──────────────────
const StatCard = ({ label, value, color, icon: Icon }) => {
  const colors = {
    green:  'bg-green-50 border-green-200 text-green-700',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-700',
    red:    'bg-red-50 border-red-200 text-red-700',
    gray:   'bg-gray-50 border-gray-200 text-gray-600',
    blue:   'bg-blue-50 border-blue-200 text-blue-700',
  };
  return (
    <div className={`rounded-xl border-2 p-4 ${colors[color] || colors.gray}`}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium opacity-80">{label}</p>
          <p className="text-3xl font-bold mt-1">{value}</p>
        </div>
        {Icon && <Icon className="h-8 w-8 opacity-40" />}
      </div>
    </div>
  );
};

// ─── BH0 status badge ───────────────────────────────────────────────────
const getBh0StatusBadge = (status) => {
  const map = {
    active:        { classes: 'bg-red-100 text-red-700 border-red-300', label: 'Active' },
    appealed:      { classes: 'bg-amber-100 text-amber-700 border-amber-300', label: 'Appealed' },
    appeal_upheld: { classes: 'bg-red-100 text-red-800 border-red-400', label: 'Upheld' },
    reversed:      { classes: 'bg-green-100 text-green-700 border-green-300', label: 'Reversed' },
  };
  const c = map[status] || { classes: 'bg-gray-100 text-gray-700 border-gray-300', label: status || 'Unknown' };
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full border ${c.classes}`}>
      {c.label}
    </span>
  );
};

// ─── Calendar cell color helper ─────────────────────────────────────────
const getCellStyle = (dayData, isFuture) => {
  if (isFuture) return 'bg-white text-gray-300';
  if (!dayData) return 'bg-gray-100 text-gray-400';

  // BH0 states take priority
  if (dayData.bh0) {
    const bh0Status = dayData.bh0_status || 'active';
    if (bh0Status === 'appealed') return 'bg-amber-900 text-amber-400 ring-1 ring-amber-400 hover:bg-amber-800';
    if (bh0Status === 'reversed') return 'bg-green-500 text-white ring-1 ring-green-300 hover:bg-green-600';
    if (bh0Status === 'appeal_upheld') return 'bg-slate-900 text-red-400 ring-1 ring-red-600 hover:bg-slate-800';
    return 'bg-slate-900 text-red-500 ring-1 ring-red-500 hover:bg-slate-800'; // active
  }

  switch (dayData.status) {
    case 'present':
    case 'checked_in':
      return 'bg-green-500 text-white hover:bg-green-600';
    case 'half_day':
      return 'bg-yellow-400 text-yellow-900 hover:bg-yellow-500';
    case 'absent':
      return 'bg-red-500 text-white hover:bg-red-600';
    case 'weekly_off':
      return 'bg-gray-200 text-gray-500';
    case 'late':
      return 'bg-amber-400 text-amber-900 hover:bg-amber-500';
    default:
      return 'bg-gray-100 text-gray-400';
  }
};

// ─── Cell tooltip ───────────────────────────────────────────────────────
const getCellTooltip = (dayData) => {
  if (!dayData) return 'No data';
  if (dayData.bh0) {
    const bh0Status = dayData.bh0_status || 'active';
    return `BH0 ${bh0Status.replace('_', ' ')}${dayData.bh0_reason ? ` - Reason: ${dayData.bh0_reason}` : ''}`;
  }
  const statusLabel = (dayData.status || 'unknown').replace(/_/g, ' ');
  const hours = dayData.hours != null ? ` - ${dayData.hours}h` : '';
  return `${statusLabel}${hours}`;
};

// ─── Role display helper ────────────────────────────────────────────────
const getRoleLabel = (role) => {
  const names = { team_leader: 'TL', fitness_manager: 'FM', manager: 'Mgr' };
  return names[role] || role;
};

// ═════════════════════════════════════════════════════════════════════════
// BH0 Payroll Tab Component
// ═════════════════════════════════════════════════════════════════════════
export default function BH0PayrollTab({ onMarkBH0, onRefreshRef }) {
  const now = new Date();
  const [bh0Month, setBh0Month] = useState(now.getMonth());
  const [bh0Year, setBh0Year] = useState(now.getFullYear());
  const [calendarData, setCalendarData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedUserId, setExpandedUserId] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  const monthStr = `${bh0Year}-${String(bh0Month + 1).padStart(2, '0')}`;
  const daysInMonth = getDaysInMonth(new Date(bh0Year, bh0Month, 1));
  const todayDate = new Date();
  const todayDay = todayDate.getFullYear() === bh0Year && todayDate.getMonth() === bh0Month ? todayDate.getDate() : null;

  // ── Fetch data ──
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [calRes, sumRes] = await Promise.all([
        axiosInstance.get('/attendance/daily/bh0/calendar', { params: { month: monthStr } }),
        axiosInstance.get('/attendance/daily/bh0/monthly-summary', { params: { month: monthStr } }),
      ]);
      setCalendarData(calRes.data);
      setSummaryData(sumRes.data);
    } catch (err) {
      console.error('Failed to load BH0 payroll data:', err);
      toast.error('Failed to load BH0 payroll data');
    } finally {
      setLoading(false);
    }
  }, [monthStr]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Expose refresh callback to parent
  useEffect(() => {
    if (onRefreshRef) {
      onRefreshRef.current = fetchData;
    }
  }, [fetchData, onRefreshRef]);

  // ── Month navigation ──
  const goToPrevMonth = () => {
    if (bh0Month === 0) { setBh0Month(11); setBh0Year(y => y - 1); }
    else { setBh0Month(m => m - 1); }
    setExpandedUserId(null);
  };
  const goToNextMonth = () => {
    if (bh0Month === 11) { setBh0Month(0); setBh0Year(y => y + 1); }
    else { setBh0Month(m => m + 1); }
    setExpandedUserId(null);
  };

  // ── Derive days array ──
  const days = useMemo(() => Array.from({ length: daysInMonth }, (_, i) => i + 1), [daysInMonth]);

  // ── Check weekend ──
  const isWeekend = (day) => {
    const d = new Date(bh0Year, bh0Month, day).getDay();
    return d === 0 || d === 6;
  };

  // ── Summary stats ──
  const summary = useMemo(() => {
    if (!summaryData) return { total_staff: 0, present_today: 0, bh0_this_month: 0, pending_appeals: 0, avg_billing_hours: 0 };
    // Try to read from summary field, or compute from users
    const s = summaryData.totals || summaryData.summary || {};
    const users = summaryData.users || [];
    return {
      total_staff: s.total_users || users.length,
      present_today: s.present_today || 0,
      bh0_this_month: s.total_bh0_days || users.reduce((acc, u) => acc + (u.bh0_days || u.bh0_total || 0), 0),
      pending_appeals: s.total_bh0_appealed || users.reduce((acc, u) => acc + (u.bh0_appealed || 0), 0),
      avg_billing_hours: s.avg_billing_hours || (users.length > 0
        ? Math.round(users.reduce((acc, u) => acc + (u.net_billing_hours || 0), 0) / users.length * 10) / 10
        : 0),
    };
  }, [summaryData]);

  // ── Filter + search users ──
  const filteredUsers = useMemo(() => {
    const users = calendarData?.users || [];
    return users.filter(u => {
      const matchesRole = roleFilter === 'all' || u.user_role === roleFilter;
      const matchesSearch = !searchQuery || (u.user_name || '').toLowerCase().includes(searchQuery.toLowerCase());
      return matchesRole && matchesSearch;
    });
  }, [calendarData, roleFilter, searchQuery]);

  // ── Get day data for a user ──
  const getDayData = (user, day) => {
    if (!user.days) return null;
    const dateKey = `${bh0Year}-${String(bh0Month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return user.days[dateKey] || user.days[String(day)] || null;
  };

  // ── Get user totals ──
  const getUserTotals = (user) => {
    // From summary data if available
    const sumUsers = summaryData?.users || [];
    const sumUser = sumUsers.find(u => u.user_id === user.user_id);
    if (sumUser) return sumUser;
    // Fallback: compute from calendar data
    let bh0Count = 0;
    let netHours = 0;
    days.forEach(d => {
      const dd = getDayData(user, d);
      if (dd?.bh0 && dd.bh0_status !== 'reversed') bh0Count++;
      if (dd?.hours) netHours += dd.hours;
    });
    return { bh0_days: bh0Count, net_billing_hours: Math.round(netHours * 10) / 10 };
  };

  // ── Export CSV ──
  const handleExportCSV = () => {
    const sumUsers = summaryData?.users || [];
    if (sumUsers.length === 0) { toast.error('No data to export'); return; }
    const headers = ['Name', 'Role', 'Working Days', 'Present', 'Absent', 'Half Day', 'BH0 Days', 'Gross Hours', 'BH0 Deduction', 'Net Billing Hours'];
    const rows = sumUsers.map(u => [
      u.user_name, getRoleLabel(u.user_role),
      u.working_days || '', u.present_days || '', u.absent_days || '', u.half_days || '',
      u.bh0_days || 0, u.total_hours_worked || u.total_hours || '',
      u.bh0_deduction_hours || '', u.net_billing_hours || ''
    ]);
    const csv = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `bh0-payroll-${monthStr}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── Render expanded detail panel for a user ──
  const renderDetailPanel = (user) => {
    const totals = getUserTotals(user);
    const sumUsers = summaryData?.users || [];
    const sumUser = sumUsers.find(u => u.user_id === user.user_id);

    // Get BH0 records from summary data
    const bh0Records = sumUser?.bh0_records || sumUser?.bh0_dates?.map(d => ({ date: d })) || [];

    return (
      <tr key={`detail-${user.user_id}`}>
        <td colSpan={daysInMonth + 4} className="p-0">
          <div className="bg-gray-50 border-t border-b border-gray-200 p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Column 1: Monthly Summary */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Monthly Summary</h4>
                <div className="space-y-2">
                  {[
                    ['Working Days', totals.working_days],
                    ['Present Days', totals.present_days],
                    ['Absent Days', totals.absent_days],
                    ['Half Days', totals.half_days],
                    ['BH0 Days', totals.bh0_days || 0],
                    ['Weekly Offs', totals.weekly_off_days || totals.weekly_offs],
                  ].filter(([, v]) => v !== undefined).map(([label, val]) => (
                    <div key={label} className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">{label}</span>
                      <span className="font-semibold">{val}</span>
                    </div>
                  ))}
                  <div className="border-t border-gray-200 pt-2 mt-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Gross Hours</span>
                      <span className="font-semibold">{totals.total_hours_worked || totals.total_hours || '-'}h</span>
                    </div>
                    <div className="flex items-center justify-between text-sm mt-1">
                      <span className="text-gray-600 font-medium">Net Billing Hours</span>
                      <span className="text-lg font-bold text-[#7C1D3E]">{totals.net_billing_hours || 0}h</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Column 2: Mini Calendar Heatmap */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 mb-3">Calendar Overview</h4>
                <div className="grid grid-cols-7 gap-1 mb-1">
                  {['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((d, i) => (
                    <div key={i} className="w-8 h-6 flex items-center justify-center text-xs font-medium text-gray-500">{d}</div>
                  ))}
                </div>
                <div className="grid grid-cols-7 gap-1">
                  {/* Empty cells before first day */}
                  {Array.from({ length: getDay(new Date(bh0Year, bh0Month, 1)) }, (_, i) => (
                    <div key={`e-${i}`} className="w-8 h-8" />
                  ))}
                  {days.map(d => {
                    const dd = getDayData(user, d);
                    const isFuture = bh0Year === todayDate.getFullYear() && bh0Month === todayDate.getMonth() && d > todayDate.getDate();
                    return (
                      <div
                        key={d}
                        className={`w-8 h-8 rounded flex items-center justify-center text-xs font-medium ${getCellStyle(dd, isFuture)}`}
                        title={isFuture ? 'Future' : getCellTooltip(dd)}
                      >
                        {dd?.bh0 && dd.bh0_status !== 'reversed' ? (
                          <Shield className="h-3 w-3" />
                        ) : d}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Column 3: BH0 Entries */}
              <div>
                <h4 className="text-sm font-semibold text-gray-700 flex items-center gap-2 mb-3">
                  <Shield className="h-4 w-4 text-red-600" />
                  BH0 Entries
                </h4>
                {bh0Records.length === 0 ? (
                  <p className="text-sm text-gray-500 text-center py-4">No BH0 entries this month</p>
                ) : (
                  <div className="space-y-3">
                    {bh0Records.map((p, idx) => (
                      <div key={idx} className="border rounded-lg p-3 bg-white">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-semibold">{p.date}</span>
                          {getBh0StatusBadge(p.status)}
                        </div>
                        {p.reason && <p className="text-xs text-gray-600">Reason: {p.reason}</p>}
                        {p.marked_by_name && <p className="text-xs text-gray-500">Marked by {p.marked_by_name}</p>}
                      </div>
                    ))}
                  </div>
                )}
                {/* Mark BH0 button */}
                {onMarkBH0 && (
                  <div className="mt-4 flex justify-end">
                    <Button
                      onClick={() => onMarkBH0(user.user_id, user.user_name)}
                      className="text-xs border-red-300 text-red-700 hover:bg-red-50"
                      variant="outline"
                      size="sm"
                    >
                      <Shield className="h-3 w-3 mr-1" />
                      Mark BH0
                    </Button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  // ═════════════════════════════════════════════════════════════════════
  // Render
  // ═════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <StatCard label="Total Staff" value={summary.total_staff} color="gray" icon={Users} />
        <StatCard label="Present Today" value={summary.present_today} color="green" icon={UserCheck} />
        <StatCard label="BH0 This Month" value={summary.bh0_this_month} color="red" icon={Shield} />
        <StatCard label="Pending Appeals" value={summary.pending_appeals} color="yellow" icon={AlertTriangle} />
        <StatCard label="Avg Billing Hours" value={summary.avg_billing_hours} color="blue" icon={Timer} />
      </div>

      {/* Controls Bar */}
      <div className="flex items-center gap-4 flex-wrap">
        {/* Month navigation */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goToPrevMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="text-sm font-semibold min-w-[140px] text-center">
            {MONTH_NAMES[bh0Month]} {bh0Year}
          </span>
          <Button variant="outline" size="icon" onClick={goToNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>

        {/* Role filter */}
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Role:</span>
          <Select value={roleFilter} onValueChange={setRoleFilter}>
            <SelectTrigger className="w-36">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="team_leader">Team Leader</SelectItem>
              <SelectItem value="fitness_manager">Fitness Manager</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search staff..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-48 pl-8"
          />
        </div>

        {/* Right side actions */}
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExportCSV} disabled={loading}>
            <Download className="h-4 w-4 mr-1.5" />
            Export CSV
          </Button>
          <Button variant="outline" size="sm" onClick={fetchData} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-1.5 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Calendar Grid */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <Users className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No staff data available for {MONTH_NAMES[bh0Month]} {bh0Year}</p>
            </div>
          ) : (
            <>
              {/* Desktop: Full calendar grid */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-medium text-sm sticky left-0 bg-gray-50 z-10 min-w-[180px]">Staff</th>
                      <th className="text-left p-2 font-medium text-sm sticky left-[180px] bg-gray-50 z-10 w-16">Role</th>
                      {days.map(d => (
                        <th
                          key={d}
                          className={`text-center p-1 font-medium text-xs w-10
                            ${todayDay === d ? 'bg-[#7C1D3E]/10 text-[#7C1D3E] font-bold' : ''}
                            ${isWeekend(d) ? 'bg-gray-100' : ''}
                          `}
                        >
                          {d}
                        </th>
                      ))}
                      <th className="text-center p-3 font-medium text-sm bg-red-50 w-16">BH0</th>
                      <th className="text-right p-3 font-medium text-sm w-20">Net Hrs</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map(user => {
                      const totals = getUserTotals(user);
                      const bh0Count = totals.bh0_days || totals.bh0_total || 0;
                      const netHours = totals.net_billing_hours || 0;
                      const isExpanded = expandedUserId === user.user_id;
                      return (
                        <React.Fragment key={user.user_id}>
                          <tr
                            className={`border-b hover:bg-gray-50 transition-colors cursor-pointer ${isExpanded ? 'bg-blue-50/30' : ''}`}
                            onClick={() => setExpandedUserId(isExpanded ? null : user.user_id)}
                          >
                            {/* Staff name */}
                            <td className="p-3 sticky left-0 bg-white z-10">
                              <div className="flex items-center gap-2">
                                {isExpanded ? <ChevronUp className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" /> : <ChevronDown className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />}
                                <span className="font-medium text-sm">{user.user_name}</span>
                                {bh0Count > 0 && (
                                  <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0" title="Has BH0 this month" />
                                )}
                              </div>
                            </td>
                            {/* Role */}
                            <td className="p-2 sticky left-[180px] bg-white z-10">
                              <span className="px-2 py-1 bg-gray-100 rounded text-xs font-medium">
                                {getRoleLabel(user.user_role)}
                              </span>
                            </td>
                            {/* Day cells */}
                            {days.map(d => {
                              const dd = getDayData(user, d);
                              const isFuture = bh0Year === todayDate.getFullYear() && bh0Month === todayDate.getMonth() && d > todayDate.getDate();
                              return (
                                <td key={d} className="p-0.5 text-center">
                                  <div
                                    className={`w-9 h-9 mx-auto rounded-md flex items-center justify-center text-xs font-medium transition-colors ${getCellStyle(dd, isFuture)}`}
                                    title={isFuture ? 'Future' : getCellTooltip(dd)}
                                    onClick={(e) => e.stopPropagation()}
                                  >
                                    {dd?.bh0 && dd.bh0_status !== 'reversed' ? (
                                      <Shield className="h-3.5 w-3.5" />
                                    ) : d}
                                  </div>
                                </td>
                              );
                            })}
                            {/* BH0 count */}
                            <td className="p-3 text-center">
                              {bh0Count > 0 ? (
                                <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">{bh0Count}</span>
                              ) : (
                                <span className="text-gray-300 text-xs">0</span>
                              )}
                            </td>
                            {/* Net hours */}
                            <td className="p-3 text-right">
                              <span className={`font-medium text-sm ${
                                netHours >= 200 ? 'text-green-700 font-bold' :
                                netHours >= 150 ? 'text-amber-600' :
                                'text-red-600 font-bold'
                              }`}>
                                {netHours}h
                              </span>
                            </td>
                          </tr>
                          {isExpanded && renderDetailPanel(user)}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile: Card list view */}
              <div className="md:hidden space-y-3 p-4">
                {filteredUsers.map(user => {
                  const totals = getUserTotals(user);
                  const bh0Count = totals.bh0_days || totals.bh0_total || 0;
                  const netHours = totals.net_billing_hours || 0;
                  const isExpanded = expandedUserId === user.user_id;
                  return (
                    <Card key={user.user_id} className="overflow-hidden">
                      <CardContent className="p-4">
                        {/* Header */}
                        <div
                          className="flex items-center justify-between mb-3 cursor-pointer"
                          onClick={() => setExpandedUserId(isExpanded ? null : user.user_id)}
                        >
                          <div>
                            <p className="font-medium text-sm">{user.user_name}</p>
                            <span className="px-2 py-0.5 bg-gray-100 rounded text-xs">{getRoleLabel(user.user_role)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            {bh0Count > 0 && (
                              <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">
                                {bh0Count} BH0
                              </span>
                            )}
                            {isExpanded ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                          </div>
                        </div>

                        {/* Mini calendar strip */}
                        <div className="flex gap-1 overflow-x-auto pb-2 snap-x">
                          {days.map(d => {
                            const dd = getDayData(user, d);
                            const isFuture = bh0Year === todayDate.getFullYear() && bh0Month === todayDate.getMonth() && d > todayDate.getDate();
                            return (
                              <div
                                key={d}
                                className={`flex-shrink-0 w-8 h-8 rounded-md flex items-center justify-center text-xs font-medium ${getCellStyle(dd, isFuture)}`}
                                title={isFuture ? 'Future' : getCellTooltip(dd)}
                              >
                                {dd?.bh0 && dd.bh0_status !== 'reversed' ? (
                                  <Shield className="h-3 w-3" />
                                ) : d}
                              </div>
                            );
                          })}
                        </div>

                        {/* Summary row */}
                        <div className="flex items-center gap-4 text-xs text-gray-600 mt-2 pt-2 border-t">
                          <span>Present: {totals.present_days || '-'}</span>
                          <span>BH0: {bh0Count}</span>
                          <span className="ml-auto font-semibold">Net: {netHours}h</span>
                        </div>

                        {/* Expanded detail */}
                        {isExpanded && (
                          <div className="mt-4 pt-4 border-t border-gray-200 space-y-4">
                            {/* Summary stats */}
                            <div className="space-y-2">
                              {[
                                ['Working Days', totals.working_days],
                                ['Present Days', totals.present_days],
                                ['Absent Days', totals.absent_days],
                                ['BH0 Days', bh0Count],
                              ].filter(([, v]) => v !== undefined).map(([label, val]) => (
                                <div key={label} className="flex items-center justify-between text-sm">
                                  <span className="text-gray-600">{label}</span>
                                  <span className="font-semibold">{val}</span>
                                </div>
                              ))}
                              <div className="flex items-center justify-between text-sm font-medium pt-2 border-t">
                                <span className="text-gray-700">Net Billing Hours</span>
                                <span className="text-[#7C1D3E] font-bold">{netHours}h</span>
                              </div>
                            </div>
                            {/* Mark BH0 button */}
                            {onMarkBH0 && (
                              <div className="flex justify-end">
                                <Button
                                  onClick={() => onMarkBH0(user.user_id, user.user_name)}
                                  className="text-xs border-red-300 text-red-700 hover:bg-red-50"
                                  variant="outline"
                                  size="sm"
                                >
                                  <Shield className="h-3 w-3 mr-1" />
                                  Mark BH0
                                </Button>
                              </div>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Color Legend */}
              <div className="flex flex-wrap gap-4 mt-4 px-3 py-2 bg-gray-50 rounded-lg border mx-4 mb-4">
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="w-3 h-3 rounded bg-green-500" /> Present
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="w-3 h-3 rounded bg-yellow-400" /> Half Day
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="w-3 h-3 rounded bg-red-500" /> Absent
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="w-3 h-3 rounded bg-slate-900 ring-1 ring-red-500" /> BH0 Active
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="w-3 h-3 rounded bg-amber-900 ring-1 ring-amber-400" /> BH0 Appealed
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="w-3 h-3 rounded bg-gray-200" /> Weekly Off
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="w-3 h-3 rounded bg-amber-400" /> Late
                </div>
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="w-3 h-3 rounded bg-white border border-gray-200" /> Future
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
